/**
 * RecommendationsCard (CRM-002) — the locked 360 mockup places this in the
 * LEFT flow between the dispatch banner and Recent service: work a tech
 * proposed that didn't sell that day, resurfaced every time the customer
 * record opens ("Brent recommended an igniter in May — want that handled
 * while we're out?"). ServiceTitan's highest-revenue feature.
 *
 * Rows are STATED facts with provenance (tech, date, customer's reaction).
 * Actions in dispatcher words: "Booked it" / "They passed" — resolved rows
 * collapse into a history line, never deleted.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { Lightbulb, Plus, Check, X, RotateCcw } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { TechLink } from "@/components/links";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import {
  useRecommendations,
  useAddRecommendation,
  useSetRecommendationStatus,
  REACTION_LABEL,
  type Recommendation,
} from "./recommendations";

/** All-dept active techs for the "proposed by" picker (pseudo holding
 *  buckets excluded — [[reference_pseudo_employees]]). */
const PSEUDO = new Set(["0000", "0001", "1000", "1001", "1002", "1003", "7777", "8888", "9999"]);
function useProposerTechs() {
  return useQuery({
    queryKey: ["proposer_techs"],
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mirror_employee")
        .select("empno, empname, firstname, lastname")
        .eq("inactive", false)
        .order("empname");
      if (error) throw error;
      return ((data ?? []) as { empno: string; empname: string | null; firstname: string | null; lastname: string | null }[])
        .filter((t) => !PSEUDO.has(t.empno))
        .map((t) => ({
          empno: t.empno,
          name: t.empname ?? (`${t.firstname ?? ""} ${t.lastname ?? ""}`.trim() || t.empno),
        }));
    },
  });
}

function money(v: number | null): string | null {
  if (v === null || v === undefined) return null;
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function age(iso: string): string {
  try {
    const days = Math.floor((Date.now() - parseISO(iso).getTime()) / 86400000);
    if (days < 1) return "today";
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${(days / 365).toFixed(1)}yr ago`;
  } catch {
    return "";
  }
}

export function RecommendationsCard({
  custno,
  locno,
  startAdding,
}: {
  custno: string;
  locno?: string | null;
  /** STRIP+A: the Overview's quiet line mounts this card mid-add. */
  startAdding?: boolean;
}) {
  const { data, isLoading } = useRecommendations(custno);
  const all = data ?? [];
  const open = all.filter((r) => r.status === "open");
  const resolved = all.filter((r) => r.status !== "open");
  const add = useAddRecommendation(custno);
  const setStatus = useSetRecommendationStatus(custno);
  const { data: techs } = useProposerTechs();

  const [adding, setAdding] = useState(!!startAdding);
  const [showResolved, setShowResolved] = useState(false);
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [tech, setTech] = useState("");
  const [reaction, setReaction] = useState("");

  function submit() {
    if (!title.trim()) return;
    add.mutate(
      {
        locno,
        title,
        est_price: price.trim() ? Number(price.replace(/[^0-9.]/g, "")) || null : null,
        tech_empno: tech || null,
        reaction: (reaction || null) as Recommendation["reaction"],
      },
      {
        onSuccess: () => {
          setTitle(""); setPrice(""); setTech(""); setReaction("");
          setAdding(false);
        },
      },
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-rose-50/70 via-transparent to-transparent px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-rose-50 text-rose-600">
            <Lightbulb className="h-3.5 w-3.5" />
          </span>
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
            Recommended work
          </span>
          {open.length > 0 && (
            <span className="rounded-full bg-rose-50 px-1.5 text-[11px] font-bold text-rose-700">
              {open.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setAdding((v) => !v)}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-brand-700 transition-colors hover:bg-brand-50 hover:text-brand-900"
        >
          <Plus className="h-3 w-3" /> Add
        </button>
      </div>

      <div className="px-4 py-3">
        {adding && (
          <div className="mb-3 space-y-2 rounded-lg bg-slate-50 p-3">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder='What was proposed — e.g. "Replace inducer motor"'
              className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none"
              autoFocus
            />
            <div className="flex gap-2">
              <input
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="Est. $"
                className="w-24 rounded-md border border-hairline px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none"
              />
              <select
                value={tech}
                onChange={(e) => setTech(e.target.value)}
                className="min-w-0 flex-1 rounded-md border border-hairline px-2 py-1.5 text-sm"
              >
                <option value="">Proposed by…</option>
                {(techs ?? []).map((t) => (
                  <option key={t.empno} value={t.empno}>{t.name}</option>
                ))}
              </select>
              <select
                value={reaction}
                onChange={(e) => setReaction(e.target.value)}
                className="min-w-0 flex-1 rounded-md border border-hairline px-2 py-1.5 text-sm"
              >
                <option value="">Customer said…</option>
                <option value="considering">Thinking about it</option>
                <option value="declined_today">No for now</option>
                <option value="asked_for_quote">Wants a quote</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!title.trim() || add.isPending}
                onClick={submit}
                className="rounded-md bg-brand-700 px-3 py-1 text-xs font-semibold text-white disabled:bg-brand-300"
              >
                {add.isPending ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={() => setAdding(false)}
                className="text-xs font-medium text-slate-500 hover:text-slate-700"
              >
                Cancel
              </button>
              {add.isError && <span className="text-xs text-red-600">Couldn&apos;t save</span>}
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="h-10 animate-pulse rounded-lg bg-slate-100" />
        ) : open.length === 0 && !adding ? (
          <p className="text-xs text-slate-400">Nothing proposed yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {open.map((r) => (
              <li key={r.id} className="group flex items-start gap-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold text-slate-900">{r.title}</span>
                    {money(r.est_price) && (
                      <span className="text-sm font-medium tabular-nums text-emerald-700">
                        {money(r.est_price)}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {r.tech_empno ? (
                      <>
                        Proposed by <TechName empno={r.tech_empno} /> {age(r.created_at)}
                      </>
                    ) : (
                      <>Logged {age(r.created_at)}</>
                    )}
                    {r.reaction && (
                      <span className="text-slate-400"> · customer was {REACTION_LABEL[r.reaction]}</span>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={() => setStatus.mutate({ id: r.id, status: "booked" })}
                    title="Booked it — the work got scheduled"
                    className="rounded-md border border-hairline bg-white px-2 py-0.5 text-[11px] font-medium text-emerald-700 transition-colors hover:bg-emerald-50"
                  >
                    <Check className="mr-0.5 inline h-3 w-3" />
                    Booked it
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatus.mutate({ id: r.id, status: "declined" })}
                    title="They passed — stops resurfacing, stays in history"
                    className="rounded-md border border-hairline bg-white px-2 py-0.5 text-[11px] font-medium text-slate-500 transition-colors hover:bg-slate-50 hover:text-red-600"
                  >
                    <X className="mr-0.5 inline h-3 w-3" />
                    They passed
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {resolved.length > 0 && (
          <div className={cn("text-[11px] text-slate-400", (open.length > 0 || adding) && "mt-2 border-t border-slate-100 pt-2")}>
            <button
              type="button"
              onClick={() => setShowResolved((v) => !v)}
              className="font-medium text-slate-400 transition-colors hover:text-slate-600"
            >
              {showResolved ? "Hide" : "Show"} {resolved.length} resolved
            </button>
            {showResolved && (
              <ul className="mt-1 space-y-1">
                {resolved.map((r) => (
                  <li key={r.id} className="flex items-center gap-2">
                    <span
                      className={cn(
                        "rounded px-1 text-[10px] font-semibold uppercase",
                        r.status === "booked" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500",
                      )}
                    >
                      {r.status}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-slate-500">{r.title}</span>
                    <span className="shrink-0">
                      {r.resolved_at ? format(parseISO(r.resolved_at), "MMM yyyy") : ""}
                    </span>
                    <button
                      type="button"
                      onClick={() => setStatus.mutate({ id: r.id, status: "open" })}
                      title="Reopen — it resurfaces again"
                      className="shrink-0 text-slate-300 transition-colors hover:text-brand-600"
                    >
                      <RotateCcw className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

/** Resolves a tech name from the picker cache (cheap — same query). */
function TechName({ empno }: { empno: string }) {
  const { data: techs } = useProposerTechs();
  const name = techs?.find((t) => t.empno === empno)?.name ?? empno;
  return <TechLink empno={empno} name={name} className="text-xs" />;
}
